import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Store as StoreIcon, ShieldCheck, Clock, AlertTriangle, XCircle, Upload,
  FileText, MapPin, Phone, Mail, Building2, CreditCard, CheckCircle2, Camera,
  Loader2, ImageIcon,
} from 'lucide-react';
import { useVendorStore } from '@/store/vendor-store';
import { PageHeader } from '@/components/shared/PageHeader';
import { ImageSpec } from '@/components/shared/ImageSpec';
import { vendorApi } from '@/services/api';
import {
  Card, CardHeader, CardTitle, CardContent, Badge, Button, Input, Separator,
} from '@nepal-ecom/ui';

type Status = 'pending' | 'under_review' | 'approved' | 'rejected' | 'suspended';

const STATUS_META: Record<Status, {
  label: string;
  variant: 'warning' | 'info' | 'success' | 'destructive' | 'secondary';
  icon: typeof Clock;
  description: string;
  tint: string;
  iconColor: string;
}> = {
  pending: {
    label: 'Pending',
    variant: 'warning',
    icon: Clock,
    description: 'Complete your KYC documents to submit for verification.',
    tint: 'bg-amber-50 ring-amber-200',
    iconColor: 'text-amber-600',
  },
  under_review: {
    label: 'Under review',
    variant: 'info',
    icon: Clock,
    description: 'Our team is reviewing your documents. Usually takes 1–2 business days.',
    tint: 'bg-cobalt-50 ring-cobalt-200',
    iconColor: 'text-cobalt-600',
  },
  approved: {
    label: 'Verified',
    variant: 'success',
    icon: ShieldCheck,
    description: 'Your store is verified and listed on Shishyastra.',
    tint: 'bg-emerald-50 ring-emerald-200',
    iconColor: 'text-emerald-600',
  },
  rejected: {
    label: 'Rejected',
    variant: 'destructive',
    icon: XCircle,
    description: 'Documents were not accepted. See note below and resubmit.',
    tint: 'bg-red-50 ring-red-200',
    iconColor: 'text-red-600',
  },
  suspended: {
    label: 'Suspended',
    variant: 'secondary',
    icon: AlertTriangle,
    description: 'Store is suspended. Contact support to resolve.',
    tint: 'bg-slate-100 ring-slate-200',
    iconColor: 'text-slate-600',
  },
};

// Keys MUST match the Vendor entity `documents` keys + the storefront register
// payload (panCard, not pan) so re-uploads land on the right key and per-doc
// rejection verdicts line up.
const KYC_DOCS = [
  { key: 'panCard', label: 'PAN / VAT certificate', hint: 'Government-issued business PAN' },
  { key: 'citizenshipFront', label: 'Citizenship — front', hint: "Owner's citizenship front page" },
  { key: 'citizenshipBack', label: 'Citizenship — back', hint: "Owner's citizenship back page" },
  { key: 'businessRegistration', label: 'Business registration (optional)', hint: 'OCR / DOI / Cottage Industry certificate' },
];

interface DocsMap {
  panCard?: string;
  citizenshipFront?: string;
  citizenshipBack?: string;
  businessRegistration?: string;
}

interface DocReview {
  status: 'pending' | 'approved' | 'rejected';
  note?: string;
}

// Older Store builds wrote the PAN under `pan`; the canonical key is `panCard`.
// Fold any legacy value forward so the checklist and re-upload target agree.
function normalizeDocs(docs: Record<string, string> | null | undefined): DocsMap {
  const { pan, panCard, ...rest } = { ...(docs ?? {}) } as Record<string, string>;
  return { ...rest, panCard: panCard || pan } as DocsMap;
}

interface BankDetails {
  bankName?: string;
  accountName?: string;
  accountNumber?: string;
}

interface VendorForm {
  businessName: string;
  slug: string;
  description: string;
  phone: string;
  email: string;
  provinceName: string;
  district: string;
  municipality: string;
  wardNumber: string;
  streetAddress: string;
  logoUrl: string;
  bannerUrl: string;
  documents: DocsMap;
  bankDetails: BankDetails;
}

const PROVINCES = ['Bagmati', 'Koshi', 'Madhesh', 'Gandaki', 'Lumbini', 'Karnali', 'Sudurpashchim'];

const empty: VendorForm = {
  businessName: '',
  slug: '',
  description: '',
  phone: '',
  email: '',
  provinceName: 'Bagmati',
  district: '',
  municipality: '',
  wardNumber: '',
  streetAddress: '',
  logoUrl: '',
  bannerUrl: '',
  documents: {},
  bankDetails: {},
};

// The public storefront lives on a different origin than this dashboard, so
// "View public page" must link to the absolute storefront URL — a relative
// /vendor/<slug> would resolve to the dashboard origin (which has no such
// route) and just bounce back to the dashboard. Baked from VITE_STOREFRONT_URL
// at build time (.env.production = https://shishyastra.com); localhost in dev.
const STOREFRONT_URL =
  (import.meta.env.VITE_STOREFRONT_URL as string | undefined) ?? 'http://localhost:3000';

export function Store() {
  const { vendor } = useVendorStore();
  const qc = useQueryClient();
  const [form, setForm] = useState<VendorForm>(empty);
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);

  // Load the vendor's own record
  const { data: vendorData } = useQuery({
    queryKey: ['vendor', 'me'],
    queryFn: async () => {
      const res = await vendorApi.getMyVendor();
      return res.data.data as any;
    },
  });

  useEffect(() => {
    if (vendorData) {
      setForm({
        businessName: vendorData.businessName ?? '',
        slug: vendorData.slug ?? '',
        description: vendorData.description ?? '',
        phone: vendorData.phone ?? '',
        email: vendorData.email ?? '',
        provinceName: vendorData.provinceName ?? 'Bagmati',
        district: vendorData.district ?? '',
        municipality: vendorData.municipality ?? '',
        wardNumber: vendorData.wardNumber ?? '',
        streetAddress: vendorData.streetAddress ?? '',
        logoUrl: vendorData.logoUrl ?? '',
        bannerUrl: vendorData.bannerUrl ?? '',
        documents: normalizeDocs(vendorData.documents),
        bankDetails: vendorData.bankDetails ?? {},
      });
    }
  }, [vendorData?.id]);

  const status: Status =
    vendorData?.verificationStatus ?? (vendor?.kycStatus ?? 'pending');
  const meta = STATUS_META[status];
  const Icon = meta.icon;

  const set = <K extends keyof VendorForm>(key: K, value: VendorForm[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const saveMutation = useMutation({
    mutationFn: async (patch: Partial<VendorForm>) => {
      if (!vendorData?.id) throw new Error('Vendor ID not loaded yet');
      return vendorApi.updateVendor(vendorData.id, patch);
    },
    onSuccess: () => {
      toast.success('Saved');
      qc.invalidateQueries({ queryKey: ['vendor', 'me'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Save failed'),
  });

  const handleUpload = async (
    key: 'logoUrl' | 'bannerUrl' | keyof DocsMap,
    file: File,
    kind: 'logo' | 'banner' | 'kyc',
  ) => {
    setUploadingKey(key);
    try {
      const fn =
        kind === 'banner' ? vendorApi.uploadBanner :
        kind === 'kyc' ? vendorApi.uploadKycDocument :
        vendorApi.uploadImage;
      const res = await fn(file);
      const url = (res.data as any).data.url;
      if (key === 'logoUrl' || key === 'bannerUrl') {
        set(key, url);
        if (vendorData?.id) await saveMutation.mutateAsync({ [key]: url } as any);
      } else {
        const nextDocs = { ...form.documents, [key]: url };
        set('documents', nextDocs);
        if (vendorData?.id) await saveMutation.mutateAsync({ documents: nextDocs });
      }
      toast.success('Uploaded');
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Upload failed');
    } finally {
      setUploadingKey(null);
    }
  };

  const completed = useMemo(() => ({
    profile: !!form.businessName && !!form.description,
    address: !!form.district && !!form.municipality,
    documents: !!form.documents.panCard && !!form.documents.citizenshipFront,
    bank: !!form.bankDetails.bankName && !!form.bankDetails.accountNumber,
  }), [form]);

  const completedCount = Object.values(completed).filter(Boolean).length;
  const progress = Math.round((completedCount / 4) * 100);

  const submitForReview = useMutation({
    mutationFn: () => {
      if (!vendorData?.id) throw new Error('Vendor ID not loaded yet');
      return vendorApi.updateVendor(vendorData.id, { verificationStatus: 'under_review' });
    },
    onSuccess: () => {
      toast.success('Submitted for review. We will email you within 1–2 business days.');
      qc.invalidateQueries({ queryKey: ['vendor', 'me'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Submit failed'),
  });

  return (
    <div className="animate-fade-in space-y-6">
      <PageHeader
        title="My Store"
        description="Manage your storefront, documents, and payout details."
        badge={<Badge variant={meta.variant} className="gap-1"><Icon className="h-3 w-3" />{meta.label}</Badge>}
        actions={
          form.slug ? (
            <Button asChild>
              <a href={`${STOREFRONT_URL}/vendor/${form.slug}`} target="_blank" rel="noreferrer">
                View public page
              </a>
            </Button>
          ) : null
        }
      />

      <Card className={`border-0 ring-1 ${meta.tint}`}>
        <CardContent className="p-6 flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="h-14 w-14 rounded-2xl bg-white shadow-card flex items-center justify-center flex-shrink-0">
            <Icon className={`h-6 w-6 ${meta.iconColor}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Verification status</p>
            <h2 className="text-xl font-bold text-slate-900 mt-1">{meta.label}</h2>
            <p className="text-sm text-slate-600 mt-1">{meta.description}</p>
          </div>
          {status === 'pending' && progress === 100 && (
            <Button onClick={() => submitForReview.mutate()} disabled={submitForReview.isPending}>
              {submitForReview.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
              Submit for review
            </Button>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Progress */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-4">
                <CardTitle>KYC checklist</CardTitle>
                <span className="text-xs text-slate-500 tabular-nums">{completedCount}/4 complete</span>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-primary-500 to-primary-600 rounded-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                {[
                  { key: 'profile', label: 'Store profile', icon: StoreIcon },
                  { key: 'address', label: 'Address', icon: MapPin },
                  { key: 'documents', label: 'Documents', icon: FileText },
                  { key: 'bank', label: 'Bank account', icon: CreditCard },
                ].map((step) => {
                  const done = completed[step.key as keyof typeof completed];
                  const StepIcon = step.icon;
                  return (
                    <div key={step.key} className={`rounded-xl border p-3 flex items-center gap-3 ${done ? 'border-emerald-200 bg-emerald-50/50' : 'border-slate-200'}`}>
                      <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${done ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-50 text-slate-500'}`}>
                        {done ? <CheckCircle2 className="h-5 w-5" /> : <StepIcon className="h-5 w-5" />}
                      </div>
                      <p className="font-semibold text-slate-800">{step.label}</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Store profile */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><StoreIcon className="h-4 w-4 text-primary-600" />Store profile</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Banner */}
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs font-semibold text-slate-700">Store banner</p>
                  <ImageSpec spec="storeBanner" />
                </div>
                {form.bannerUrl ? (
                  <div className="relative rounded-xl overflow-hidden ring-1 ring-slate-200 aspect-[5/1] bg-slate-100">
                    <img src={form.bannerUrl} alt="" className="w-full h-full object-cover" />
                    <label className="absolute bottom-3 right-3 cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) handleUpload('bannerUrl', f, 'banner');
                        }}
                      />
                      <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-white/90 backdrop-blur text-xs font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-white">
                        {uploadingKey === 'bannerUrl' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Camera className="h-3 w-3" />} Change banner
                      </span>
                    </label>
                  </div>
                ) : (
                  <label className="relative rounded-xl overflow-hidden ring-1 ring-slate-200 aspect-[5/1] bg-gradient-to-br from-primary-100 via-primary-50 to-white flex items-center justify-center cursor-pointer hover:bg-primary-50">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleUpload('bannerUrl', f, 'banner');
                      }}
                    />
                    <span className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-white text-sm font-medium text-slate-700 ring-1 ring-slate-200">
                      {uploadingKey === 'bannerUrl' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload banner
                    </span>
                  </label>
                )}
              </div>

              {/* Logo */}
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs font-semibold text-slate-700">Store logo</p>
                  <ImageSpec spec="storeLogo" />
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-20 w-20 rounded-xl ring-1 ring-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                    {form.logoUrl ? (
                      <img src={form.logoUrl} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <StoreIcon className="h-8 w-8 text-slate-300" />
                    )}
                  </div>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleUpload('logoUrl', f, 'logo');
                      }}
                    />
                    <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50">
                      {uploadingKey === 'logoUrl' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Camera className="h-3 w-3" />}
                      {form.logoUrl ? 'Replace logo' : 'Upload logo'}
                    </span>
                  </label>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-700">Store name</label>
                  <Input value={form.businessName} onChange={(e) => set('businessName', e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-700">Store URL</label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-slate-200 bg-slate-50 text-slate-500 text-xs">
                      shishyastra.com/vendor/
                    </span>
                    <Input
                      value={form.slug}
                      onChange={(e) => set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, '-'))}
                      className="rounded-l-none font-mono"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-700">About your store</label>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => set('description', e.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-4 focus:ring-primary-500/15 focus:border-primary-400"
                  placeholder="Tell customers what makes your store special — what you sell, where you're based, and what you stand for."
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-700"><Phone className="inline h-3 w-3 mr-1" />Phone</label>
                  <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-700"><Mail className="inline h-3 w-3 mr-1" />Email</label>
                  <Input value={form.email} onChange={(e) => set('email', e.target.value)} />
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={() =>
                    saveMutation.mutate({
                      businessName: form.businessName,
                      slug: form.slug,
                      description: form.description,
                      phone: form.phone,
                      email: form.email,
                    })
                  }
                  disabled={saveMutation.isPending}
                >
                  {saveMutation.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                  Save profile
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Address */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><MapPin className="h-4 w-4 text-primary-600" />Pickup address</CardTitle>
            </CardHeader>
            <CardContent className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-700">Province</label>
                <select
                  value={form.provinceName}
                  onChange={(e) => set('provinceName', e.target.value)}
                  className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm shadow-xs"
                >
                  {PROVINCES.map((p) => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-700">District</label>
                <Input value={form.district} onChange={(e) => set('district', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-700">Municipality</label>
                <Input value={form.municipality} onChange={(e) => set('municipality', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-700">Ward number</label>
                <Input value={form.wardNumber} onChange={(e) => set('wardNumber', e.target.value)} />
              </div>
              <div className="sm:col-span-2 space-y-1.5">
                <label className="text-xs font-semibold text-slate-700">Street / landmark</label>
                <Input
                  value={form.streetAddress}
                  onChange={(e) => set('streetAddress', e.target.value)}
                  placeholder="Building, street, landmark"
                />
              </div>
              <div className="sm:col-span-2 flex justify-end">
                <Button
                  onClick={() =>
                    saveMutation.mutate({
                      provinceName: form.provinceName,
                      district: form.district,
                      municipality: form.municipality,
                      wardNumber: form.wardNumber,
                      streetAddress: form.streetAddress,
                    })
                  }
                  disabled={saveMutation.isPending}
                >
                  Save address
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Documents */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><FileText className="h-4 w-4 text-primary-600" />Documents</CardTitle>
              <p className="text-xs text-slate-500 mt-1">PDF or image, up to 10MB each. Encrypted and never shared with customers.</p>
            </CardHeader>
            <CardContent className="space-y-3">
              <ImageSpec spec="document" variant="card" />
              {KYC_DOCS.map((doc) => {
                const url = form.documents[doc.key as keyof DocsMap];
                const uploading = uploadingKey === doc.key;
                const review: DocReview | undefined = vendorData?.documentReviews?.[doc.key];
                const rejected = review?.status === 'rejected';
                return (
                  <label
                    key={doc.key}
                    className={`rounded-xl border border-dashed transition-colors p-4 flex items-center gap-3 cursor-pointer ${
                      rejected
                        ? 'border-red-300 bg-red-50/60 hover:border-red-400'
                        : 'border-slate-200 hover:border-primary-400 hover:bg-primary-50/30'
                    }`}
                  >
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleUpload(doc.key as keyof DocsMap, f, 'kyc');
                      }}
                    />
                    <div className={`h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 ring-1 ${
                      rejected ? 'bg-red-50 ring-red-200 text-red-600'
                      : url ? 'bg-emerald-50 ring-emerald-200 text-emerald-600'
                      : 'bg-slate-50 ring-slate-200 text-slate-500'}`}>
                      {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> :
                        rejected ? <XCircle className="h-5 w-5" /> :
                        url ? <CheckCircle2 className="h-5 w-5" /> :
                        <Upload className="h-5 w-5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900">{doc.label}</p>
                      {rejected ? (
                        <p className="text-xs text-red-600 mt-0.5 font-medium">
                          Rejected{review?.note ? `: ${review.note}` : ''} — click to re-upload
                        </p>
                      ) : (
                        <p className="text-xs text-slate-500 mt-0.5">{url ? 'Uploaded — click to replace' : doc.hint}</p>
                      )}
                    </div>
                    {url && (
                      <button
                        type="button"
                        onClick={async (e) => {
                          // KYC lives in private storage — resolve a short-lived
                          // view URL instead of linking the raw object key.
                          e.preventDefault();
                          e.stopPropagation();
                          if (!vendorData?.id) return;
                          const viewUrl = await vendorApi
                            .getKycViewUrl(vendorData.id, doc.key)
                            .catch(() => '');
                          if (viewUrl) window.open(viewUrl, '_blank', 'noopener');
                          else toast.error('Could not open document');
                        }}
                        className={`text-xs hover:underline ${rejected ? 'text-red-600' : 'text-primary-600'}`}
                      >
                        View
                      </button>
                    )}
                  </label>
                );
              })}
            </CardContent>
          </Card>
        </div>

        <aside className="space-y-6">
          <Card className="bg-gradient-to-br from-primary-600 to-primary-800 text-white border-0">
            <CardContent className="p-6">
              <Building2 className="h-7 w-7 text-secondary-200 mb-3" />
              <h3 className="font-bold text-lg tracking-tight">Why we verify</h3>
              <p className="text-sm text-white/80 mt-2 leading-relaxed">
                Shishyastra is built on trust. Verified stores get the
                <span className="font-semibold text-secondary-200"> Verified </span>
                badge, higher search placement, and customer confidence.
              </p>
              <Separator className="my-4 bg-white/20" />
              <ul className="space-y-2 text-xs text-white/85">
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-secondary-200 flex-shrink-0" /> Get the green "Verified" badge</li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-secondary-200 flex-shrink-0" /> Priority listing in search</li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-secondary-200 flex-shrink-0" /> Eligible for weekly payouts</li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-secondary-200 flex-shrink-0" /> Access to ad and promo tools</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Bank for payouts</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-700">Bank name</label>
                <Input
                  placeholder="e.g. Nabil Bank"
                  value={form.bankDetails.bankName ?? ''}
                  onChange={(e) => set('bankDetails', { ...form.bankDetails, bankName: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-700">Account name</label>
                <Input
                  placeholder="Account holder name"
                  value={form.bankDetails.accountName ?? ''}
                  onChange={(e) => set('bankDetails', { ...form.bankDetails, accountName: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-700">Account number</label>
                <Input
                  placeholder="Account number"
                  value={form.bankDetails.accountNumber ?? ''}
                  onChange={(e) => set('bankDetails', { ...form.bankDetails, accountNumber: e.target.value })}
                />
              </div>
              <Button
                className="w-full"
                onClick={() => saveMutation.mutate({ bankDetails: form.bankDetails })}
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                Save bank details
              </Button>
            </CardContent>
          </Card>
        </aside>
      </div>
    </div>
  );
}
